import { Code, CodeService } from "@/services/CodeService";
import { Button } from "../ui/button";
import { useEffect, useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Action } from "@/services/ActionService";
import { formatString } from "@/lib/string-utils";
import { priorityColors, actionStatusColors } from "@/lib/task-utils";
import { Project, ProjectService } from "@/services/ProjectService";
import { Link, useParams } from "react-router";
import { useForm, useWatch } from "react-hook-form";
import { setFormErrors } from "@/lib/react-form-utils";
import Header from "../Header";
import { featureTypeEditUrlBuilder, featureUrlBuilder, projectEditUrlBuilder } from "@/lib/url-builder";
import { FeatureService } from "@/services/FeatureService";
import type { Feature } from "@/lib/models";
import { FeatureTypeService, type FeatureType } from "@/services/FeatureTypeService";
import { User, UserService } from "@/services/UserService";
import { FileUploadCopied } from "../ui/FilesUploadCopied";
import { AttachmentService, AttachmentType, type AttachmentDetails } from "@/services/AttachmenteService";
import ImageList from "../ImageList";
import { ValidationError } from "@/types/problemDetails";

export interface ActionEditProps {
  action: Action,
  primaryButtonText: string;
  onSave: (action: Action) => Promise<void>;
}

export const ActionForm: React.FC<ActionEditProps> = ({
  action,
  primaryButtonText,
  onSave
}) => {
  const params = useParams();
  const [codes, setCodes] = useState<Code[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [featureList, setFeatureList] = useState<Feature[]>([])
  const [featureTypeList, setFeatureTypeList] = useState<FeatureType[]>([])
  const [userList, setUserList] = useState<User[]>([])
  const {register, handleSubmit, formState: { isDirty, errors }, reset, control, setError } = useForm<Omit<Action, "id">>();
  const [priorityCodeId, statusCodeId, projectId, featureId, featureTypeId] = useWatch({
    control,
    name: ["priorityCodeId", "statusCodeId", "projectId", "featureId", "featureTypeId"],
  })
  const isSaveDisabled = !isDirty;
  const [actionName, setActionName] = useState("");
  const [attachmentDetails, setAttachmentDetails] = useState<AttachmentDetails[]>([]);
  
  const resetAction = (action: Action) => {
    reset({ 
      description: action.description,
      endDate: formatString(action.endDate?.toString()),
      name: action.name,
      featureTypeId: action.featureTypeId ?? null,
      featureId: action.featureId ?? null,
      projectId: action.projectId,
      priorityCodeId: action.priorityCodeId,
      startDate: formatString(action.startDate?.toString()),
      dueDate: formatString(action.dueDate?.toString()),
      latitude: action.latitude,
      longitude: action.longitude,
      statusCodeId: action.statusCodeId,
      siteId: action.siteId,
      assignedUserId: action.assignedUserId,
    })
  }

  useEffect(() => { 
    const loadActtion = async () => {
      if (!action) {
        return
      }

      const [codes, project, features, featureTypes, users, attachments]  = await Promise.all([
        CodeService.getCodes2(),
        ProjectService.getBySiteId(params.siteId!),
        FeatureService.getBySiteId(params.siteId!),
        FeatureTypeService.getBySiteId(params.siteId!),
        UserService.getBySiteId(params.siteId!),
        action?.id ? AttachmentService.getByEntityId(action.id) : Promise.resolve([]),
      ])

      setCodes(codes)
      setFeatureList(features)
      setProjects(project)
      setActionName(action.name)
      setFeatureTypeList(featureTypes)
      setUserList(users)
      setAttachmentDetails(attachments)
      resetAction(action)
    }
    loadActtion()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, params.siteId])

  const onSubmit = async (formData: Omit<Action, "id">) => {
    const updatedAction: Action = {
      id: action.id,
      ...formData,
      projectId: formData.projectId === '' ? null : formData.projectId,
      featureTypeId: formData.featureTypeId === '' ? null : formData.featureTypeId,
      featureId: formData.featureId === '' ? null : formData.featureId,
      assignedUserId: formData.assignedUserId === '' ? null : formData.assignedUserId,
      siteId: action.siteId,
      tenantId: action.tenantId,
    };

    try {
        await onSave(updatedAction);
        setActionName(formData.name)
        resetAction(updatedAction)
    } catch (error) {
        if (error instanceof ValidationError) {
            setFormErrors<Omit<Action, "id">>(error.errors, setError, formData)
        }
    }
  };

  return (
    <>
      <Header headerTitle={`Task:   ${actionName}`} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Name *
          </label>
          <Input
            type="text"
            {...register("name", { required: 'Name is required'})}
          />
          <span className="text-sm text-red-500">{errors.name?.message}</span>
        </div>
        <div>
          <Label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700"
          >
            Instructions
          </Label>
          <Textarea
            id="description"
            maxLength={4000}
            {...register("description", {
              maxLength: { value: 4000, message: "Description is too long" },
            })}
          />
          <span className="text-sm text-red-500">{errors.description?.message}</span>
        </div>
        <div>
          <Label
            htmlFor="projectId"
            className="block text-sm font-medium text-gray-700"
          >
            {projectId ? <Link className="text-blue-500" to={projectEditUrlBuilder(params.tenantId!, params.siteId!, projectId)}>Project</Link> : <>Project</>}
          </Label>
          <select
            id="projectId"
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            {...register("projectId")}
          >
            <option value="">Select project</option>
            {projects
              .map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Label
            htmlFor="featureTypeId"
            className="block text-sm font-medium text-gray-700"
          >
            {featureTypeId ? <Link className="text-blue-500" to={featureTypeEditUrlBuilder(params.tenantId!, params.siteId!, featureTypeId)}>Feature Type</Link> : <>Feature Type</>}
          </Label>
          <select
            id="featureTypeId"
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            {...register("featureTypeId")}
          >
            <option value="">Select feature</option>
            {featureTypeList
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((featureType) => (
                <option key={featureType.id} value={featureType.id!}>
                  {featureType.name}
                </option>
              ))}
          </select>
        </div>          
        <div>
          <Label
            htmlFor="featureId"
            className="block text-sm font-medium text-gray-700"
          >
            {featureId ? <Link className="text-blue-500" to={featureUrlBuilder(params.tenantId!, params.siteId!, featureId!)}>Feature</Link> : <>Feature</>}
          </Label>
          <select
            id="featureId"
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            {...register("featureId")}
          >
            <option value="">Select feature</option>
            {featureList
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((feature) => (
                <option key={feature.id} value={feature.id!}>
                  {feature.name}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Label
            htmlFor="assignedUserId"
            className="block text-sm font-medium text-gray-700"
          >
            Assigned User
          </Label>
          <select
            id="assignedUserId"
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            {...register("assignedUserId")}
          >
            <option value="">Select user</option>
            {userList
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
          </select>
        </div>        
        <div>
          <label
            htmlFor="priorityCodeId"
            className="block text-sm font-medium text-gray-700"
          >
            Priority
          </label>
          <select
            className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 ${priorityColors[priorityCodeId]}`}
            {...register("priorityCodeId", { required: 'Status is required'})}
          >
              {codes.length > 0 && (
                <>
                  {codes
                    .filter((c) => c.type === "ActionPriority")
                    .map((priorityCode) => (
                      <option key={priorityCode.id} value={priorityCode.id} className={priorityColors[priorityCode.id]}>
                        {priorityCode.name}
                      </option>
                    ))}
                </>
              )}
          </select>
        </div>
        <div>
          <Label
            htmlFor="statusCodeId"
            className="block text-sm font-medium text-gray-700"
          >
            Status
          </Label>
          <select
            id="statusCodeId"
            className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 ${actionStatusColors[statusCodeId]}`}
            {...register("statusCodeId", { required: 'Status is required'})}
          >
              {codes.length > 0 && (
                <>
                  {codes
                    .filter((c) => c.type === "ActionStatus")
                    .map((statusCode) => (
                      <option key={statusCode.id} value={statusCode.id ?? ""} className={actionStatusColors[statusCode.id]}>
                        {statusCode.name}
                      </option>
                    ))}
                </>
              )}
          </select>
        </div>
        <div>
          <Label
            htmlFor="startDate"
            className="block text-sm font-medium text-gray-700"
          >
            Start Date
          </Label>
          <Input
            type="date"
            id="startDate"
            {...register("startDate")}
            className="block"
          />
        </div>
        <div>
          <Label
            htmlFor="endDate"
            className="block text-sm font-medium text-gray-700"
          >
            End Date
          </Label>
          <Input
            type="date"
            id="endDate"
            {...register("endDate")}
            className="block"
          />
          <span className="text-sm text-red-500">{errors.endDate?.message}</span>
        </div>
        <div>
          <Label
            htmlFor="dueDate"
            className="block text-sm font-medium text-gray-700"
          >
            Due Date
          </Label>
          <Input
            type="date"
            id="dueDate"
            {...register("dueDate")}
            className="block"
          />
          <span className="text-sm text-red-500">{errors.endDate?.message}</span>
        </div>        
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={() => reset()}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={isSaveDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {primaryButtonText}
          </Button>
        </div>
      </form>
      {action?.id && (
        <>
          <div className="my-2">
            <FileUploadCopied
              attachmentType={AttachmentType.None}
              entityId={action.id}
              siteId={action.siteId}
              tenantId={action.tenantId}
              entityType="Action"
              onFileUploaded={(attachment) => {
                setAttachmentDetails((prev) => [
                  {
                    ...attachment,
                  },
                  ...prev,
                ]);
              }}
              buttonText="Select Images"
            />
          </div>
          <div className="grow">
            <div className={`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4`}>
              <ImageList
                attachmentColumn={false}
                attachments={attachmentDetails}
                onAttachmentsChange={setAttachmentDetails}
                onDeleteAttachment={(attachmentId: string) => {
                  try {
                    AttachmentService.delete(attachmentId);
                    setAttachmentDetails(prev => prev.filter(att => att.id !== attachmentId));
                  } catch (error) {
                    alert("Error deleting attachment");
                    throw error;
                  }
                }}
              />          
            </div>
          </div>
        </>
      )}      
    </>
  );
};
